export interface MarkerAABB {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface ClusterMarkerData {
  screenX: number;
  screenY: number;
  aabb: MarkerAABB;
  eligible: boolean;
}

export interface ClusterGroup {
  memberIndices: number[];
  centroidX: number;
  centroidY: number;
}

export interface ClusterResult {
  groups: ClusterGroup[];
  clusterIdByMarker: (number | null)[];
}

export const computeClusters = (
  markers: ClusterMarkerData[],
  prevClusterIdByMarker: (number | null)[],
  unmergePadding: number,
): ClusterResult => {
  const n = markers.length;
  const clusterIdByMarker: (number | null)[] = new Array(n).fill(null);
  const groups: ClusterGroup[] = [];

  if (n === 0) {
    return { groups, clusterIdByMarker };
  }

  // Union-Find with path compression and union by rank
  const parent = new Int32Array(n);
  const rank = new Uint8Array(n);
  for (let i = 0; i < n; i += 1) parent[i] = i;

  const find = (x: number): number => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]];
      x = parent[x];
    }
    return x;
  };

  const union = (a: number, b: number): void => {
    a = find(a);
    b = find(b);
    if (a === b) return;
    if (rank[a] < rank[b]) {
      const t = a;
      a = b;
      b = t;
    }
    parent[b] = a;
    if (rank[a] === rank[b]) rank[a] += 1;
  };

  // Pairwise AABB overlap test
  for (let i = 0; i < n; i += 1) {
    if (!markers[i].eligible) continue;

    for (let j = i + 1; j < n; j += 1) {
      if (!markers[j].eligible) continue;

      const a = markers[i].aabb;
      const b = markers[j].aabb;

      // Hysteresis: if both were in the same cluster last frame,
      // expand AABBs by unmergePadding so they need a clear gap to split
      const wasSameCluster =
        prevClusterIdByMarker[i] != null &&
        prevClusterIdByMarker[i] === prevClusterIdByMarker[j];
      const pad = wasSameCluster ? unmergePadding : 0;

      const overlaps =
        a.minX - pad < b.maxX + pad &&
        a.maxX + pad > b.minX - pad &&
        a.minY - pad < b.maxY + pad &&
        a.maxY + pad > b.minY - pad;

      if (overlaps) {
        union(i, j);
      }
    }
  }

  // Collect groups from Union-Find
  const groupMap = new Map<number, number[]>();
  for (let i = 0; i < n; i += 1) {
    if (!markers[i].eligible) continue;
    const root = find(i);
    let members = groupMap.get(root);
    if (!members) {
      members = [];
      groupMap.set(root, members);
    }
    members.push(i);
  }

  for (const members of groupMap.values()) {
    if (members.length < 2) continue;

    let cx = 0;
    let cy = 0;
    for (const idx of members) {
      cx += markers[idx].screenX;
      cy += markers[idx].screenY;
    }

    const groupIdx = groups.length;
    groups.push({
      memberIndices: members,
      centroidX: cx / members.length,
      centroidY: cy / members.length,
    });
    for (const idx of members) {
      clusterIdByMarker[idx] = groupIdx;
    }
  }

  return { groups, clusterIdByMarker };
};
